import Config from 'config';
import { createEvent, restore, sample } from 'effector';
import { t } from 'i18n';
import _ from 'lodash';
import $$api from 'models/api';
import $$ep from 'models/ep';
import { hideModal, showModal } from 'models/modals';
import navigate from 'models/router-gate';
import { $global, storage } from 'models/store-global';
import { $modals } from 'models/store-modals';
import { Fragment, createElement } from 'react';

import EstatePlanService from './EstatePlanService';
import ModalsService from './ModalsService';

import Charts from 'components/QuestionnaireForm/Charts/Charts';

import AlertsService from 'services/AlertsService';
import DocumentDownloadService from 'services/DocumentDownloadService';
import { fileToBase64 } from 'utils/common-helper';

import { MyStore } from 'types/Store';
import { Document } from 'types/zod/document';
import { ProductChoice } from 'types/zod/product-choice';
import { type QuestionnaireInstance } from 'types/zod/questionnaire-instance';

const openModalAssessmentAfterModalityIsClosed = createEvent<boolean>();

sample({
    source: restore(
        openModalAssessmentAfterModalityIsClosed.map(() => true),
        false,
    ),
    clock: hideModal.filter({ fn: (modalName) => modalName === 'MODALITY' }),
    filter: Boolean,
    target: [
        openModalAssessmentAfterModalityIsClosed.prepend(() => false),
        showModal.prepend(() => ({ modalName: 'MODAL_ASSESSMENT' } as const)),
    ],
});

const DashboardService = {
    getHealthcareQInstancesForInProgressTab: (
        qInstances: Record<string | number, QuestionnaireInstance>,
        originatorId: number,
    ) => {
        return _(qInstances || {})
            .values()
            .filter(({ questionnaire_type_code = '' }) =>
                [`HIPAA`, `LW`, `HCPOA`, `AHCD`, `GDPOA`].includes(questionnaire_type_code),
            )
            .filter({ status: 'In_Progress' })
            .filter({ originator_participant_id: originatorId })
            .filter(({ parent_questionnaire_instance_id }) => !parent_questionnaire_instance_id)
            .value();
    },

    startAssessment: async (openInEngine = true) => {
        storage.set((store): MyStore => ({ ...store, temp: { ...(store.temp || {}), estatePlanLoading: true } }));
        storage.set((store): MyStore => ({ ...store, temp: { ...(store.temp || {}), estatePlanLoaded: false } }));
        const result = await $$api.qInstances.createInstanceFx('EP');
        if (openInEngine) {
            navigate(`/document/EP/${result.state_code}/${result.questionnaire_instance_id}`);
        } else {
            storage.set(
                (store): MyStore => ({
                    ...store,
                    answers: {
                        ...(store.answers || {}),
                        [result.questionnaire_instance_id]: {},
                    },
                }),
            );
            const modalityIsOpened = $modals.map((state) => state.MODALITY?.isOpened === true).getState();
            if (!modalityIsOpened) {
                ModalsService.showModal('MODAL_ASSESSMENT');
            } else {
                openModalAssessmentAfterModalityIsClosed(true);
            }
            navigate('/dashboard/in-progress');
        }
        storage.set((store): MyStore => {
            const { estatePlanLoading, ...temp } = store.temp || {};
            return { ...store, temp };
        });
        storage.set((store): MyStore => ({ ...store, temp: { ...(store.temp || {}), estatePlanLoaded: true } }));
        return result;
    },

    printAllEstatePlanDocuments: async () => {
        const docs = $global.map((state) => state?.community?.docs || []).getState();
        const document = docs.find(({ document_type_code }) => document_type_code === 'Will');
        if (document) {
            await DocumentDownloadService.downloadZip(document.document_id);
        } else {
            console.error('No document found');
        }
    },

    requestEstatePlanDelete: () => {
        const estatePlan = $$ep.stores.$epQInstance.getState();
        if (!estatePlan) {
            return;
        }
        ModalsService.showModality(t('EPCard.delete'), t('EPCard.deleteConfirmation'), {
            confirmButton: t('EPCard.deleteYes'),
            closeButton: t('EPCard.deleteNo'),
            onConfirm: async () => {
                await $$api.qInstances.deleteInstanceFx({ id: estatePlan.questionnaire_instance_id });
                await EstatePlanService.loadEstatePlan();
            },
        });
    },

    requestEstatePlanUpdate: () => {
        ModalsService.showModality(
            t('EPCard.updatePlan'),
            createElement(
                Fragment,
                null,
                t('EPCard.updateConfirmation') as string,
                createElement('div', { className: 'miro-code-label' }, 'U'),
            ),
            {
                confirmButton: t('EPCard.updateYes'),
                closeButton: t('EPCard.updateNo'),
                onConfirm: EstatePlanService.updateEstatePlan,
            },
        );
    },

    requestEstatePlanCancel: () => {
        ModalsService.showModality(t('EPCard.revertToOriginal'), t('EPCard.revertConfirmation'), {
            confirmButton: t('EPCard.revertYes'),
            closeButton: t('EPCard.revertNo'),
            onConfirm: async () => {
                const estatePlan = $$ep.stores.$epQInstance.getState();
                if (!estatePlan) {
                    return;
                }
                await DashboardService.cancelEstatePlan(estatePlan.questionnaire_instance_id);
            },
        });
    },

    requestEstatePlanFinishUpdate: () => {
        const estatePlan = $$ep.stores.$epQInstance.getState();
        if (!estatePlan) {
            return;
        }
        ModalsService.showModality(t('EPCard.confirmUpdate'), t('EPCard.finishUpdateConfirmation'), {
            confirmButton: t('EPCard.finishUpdateYes'),
            closeButton: t('EPCard.finishUpdateNo'),
            onConfirm: async () => {
                navigate(
                    `/document/${estatePlan.questionnaire_type_code}/${estatePlan.state_code}/${estatePlan.questionnaire_instance_id}/continue`,
                );
            },
        });
    },

    showDiagram: async (section: string) => {
        const estatePlan = $$ep.stores.$epQInstance.getState();
        if (!estatePlan) {
            return;
        }
        const json = await $$api.qInstances.getSummaryFx({ id: estatePlan.questionnaire_instance_id, section });

        ModalsService.showModality(
            t(`ep:sections.${section}.title`),
            createElement(Charts, { json, fileName: 'diagram_int.html' }),
            {
                confirmButton: t('EPCard.sectionSummaryEdit'),
                closeButton: t('EPCard.sectionSummaryCancel'),
                onConfirm: async () => {
                    navigate(
                        `/document/${estatePlan.questionnaire_type_code}/${estatePlan.state_code}/${estatePlan.questionnaire_instance_id}/${section}`,
                    );
                },
                size: 'xl',
            },
        );
    },

    updateDocumentsList: async () => {
        const epId = $$ep.stores.$epQInstance.getState()?.questionnaire_instance_id;
        if (!epId) {
            return [];
        }
        const docs = await $$api.qInstances.getDocumentsForQInstanceFx(epId);
        storage.set(
            (store): MyStore => ({
                ...store,
                temp: {
                    ...(store.temp || {}),
                    documents_in_progress: {
                        ...(store.temp?.documents_in_progress || {}),
                        documents: docs,
                    },
                },
            }),
        );
        return docs;
    },

    uploadSignedDocument: async (documentId: Document['document_id'], file: File) => {
        if (file && file.size > Config.maxFileSizeMb * 1024 * 1024) {
            AlertsService.showError(t('validate_document.too_big', { max: Config.maxFileSizeMb }));
            return;
        }
        const filebase64 = await fileToBase64(file);
        const response = await $$api.documents.uploadScanFx({ documentId, filename: file.name, file: filebase64 });
        await DashboardService.updateDocumentsList();
        AlertsService.showSuccess(t('newDashboardPage.messages.success.uploadedSignedDocument'));
        return response;
    },

    getTakeAssessmentUrl: (): string => {
        const user = $global.map((state) => state.auth?.user).getState();
        // const isTempUser = TempUsersService.isTempUser(user?.attributes?.email);
        const qInstance = $$ep.stores.$epQInstance.getState();
        if (!qInstance) {
            return '';
        }
        if (user?.attributes && _.isEmpty(qInstance)) {
            console.warn('Estate plan is empty or not exist', qInstance);
            return `${Config?.application?.baseUrl}/dashboard/start-assessment`;
        }
        const url = user?.attributes
            ? `${Config?.application?.baseUrl}/document/${qInstance.questionnaire_type_code}/${qInstance.state_code}/${qInstance.questionnaire_instance_id}`
            : // : `${Config?.application?.baseUrl}/signup?afterlogin=/dashboard/start-assessment`;
              `${Config?.application?.baseUrl}/signup`;
        return url;
    },

    takeAssessmentButtonClick: () => {
        const url = DashboardService.getTakeAssessmentUrl();
        window.open(url, `_self`);
    },

    takePreselectedAssessmentButtonClick: (product_choice: ProductChoice) => {
        const url = DashboardService.getTakeAssessmentUrl();
        const queryParams = `?product-choice=${product_choice}`;
        window.open(url + queryParams, `_self`);
    },

    cancelEstatePlan: async (id: QuestionnaireInstance['questionnaire_instance_id']) => {
        const data = await $$api.qInstances.rollbackFx(id);
        // TODO: Check if we need this call here
        await EstatePlanService.loadEstatePlan();
        return data;
    },
};

export default DashboardService;
